import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Form,
  InputNumber,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import copyToClipboard from "../../utils/copyToClipboard";

const { Text } = Typography;

const PackingStation = ({
  shippingOrderId,
  wegeOrderId,
  alibabaOrderIds,
  onPackingComplete,
  onRefresh,
  onProceedToBundle,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [packingStatus, setPackingStatus] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  const alibabaOrderIdsKey = Array.isArray(alibabaOrderIds)
    ? alibabaOrderIds.filter(Boolean).join(",")
    : "";

  const fetchPackingStatus = async () => {
    if (
      !shippingOrderId &&
      !wegeOrderId &&
      !alibabaOrderIdsKey
    )
      return;
    setLoading(true);
    try {
      let url = "";
      if (alibabaOrderIdsKey) {
        url = `/shipping/api/v1/shipping/packages/packing-status/?alibaba_order_ids=${alibabaOrderIdsKey}`;
      } else if (wegeOrderId) {
        url = `/shipping/api/v1/shipping/packages/packing-status/?wege_order_id=${wegeOrderId}`;
      } else if (shippingOrderId) {
        url = `/shipping/api/v1/shipping/order-shipping/${shippingOrderId}/packing-status/`;
      }
      const res = await axios.get(url);
      setPackingStatus(res.data);
      if (res.data.ready_for_submission) {
        onPackingComplete?.(true);
      } else {
        onPackingComplete?.(false);
      }
    } catch (error) {
      toast.error(t("packing.fetchError", "Failed to fetch packing status"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingOrderId, wegeOrderId, alibabaOrderIdsKey]);

  const handleItemQuantityChange = (itemId, quantity, maxQuantity) => {
    const validQty = Math.min(Math.max(0, quantity), maxQuantity);
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: validQty,
      },
    }));
  };

  const handleLabelConfirmChange = (itemId, checked) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        label_changed_confirmed: checked,
      },
    }));
  };

  const handleSubmit = async (values) => {
    // Collect items that have a quantity > 0
    const itemsToPack = Object.entries(selectedItems)
      .filter(([_, data]) => data.quantity > 0)
      .map(([id, data]) => ({
        order_item_id: parseInt(id),
        quantity: data.quantity,
        label_changed_confirmed: data.label_changed_confirmed || false,
      }));

    if (itemsToPack.length === 0) {
      toast.error(
        t(
          "packing.noItemsError",
          "Please select at least one item to pack in this box.",
        ),
      );
      return;
    }

    // Validate that items requiring label confirmation (those with a note) have it checked
    const missingConfirmations = itemsToPack.filter((item) => {
      const statusItem = packingStatus?.items?.find(
        (i) => i.order_item_id === item.order_item_id,
      );
      const hasNote = !!(statusItem?.note_en || statusItem?.note_ar);
      // Need confirmation if there's a note and we haven't checked it now
      return hasNote && !item.label_changed_confirmed;
    });

    if (missingConfirmations.length > 0) {
      toast.error(
        t(
          "packing.labelConfirmError",
          "You must physically change and confirm the size label for highlighted items.",
        ),
      );
      return;
    }

    if (fileList.length === 0) {
      toast.error(
        t("packing.reqPhotoError", "A photo of the packed box is required."),
      );
      return;
    }

    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append("weight_kg", values.weight_kg);
      formData.append("length_cm", values.length_cm);
      formData.append("width_cm", values.width_cm);
      formData.append("height_cm", values.height_cm);
      formData.append("items", JSON.stringify(itemsToPack));

      if (fileList.length > 0) {
        formData.append("photo", fileList[0].originFileObj || fileList[0]);
      }

      const postUrl =
        wegeOrderId || alibabaOrderIdsKey
          ? `/shipping/api/v1/shipping/packages/`
          : `/shipping/api/v1/shipping/order-shipping/${shippingOrderId}/packages/`;

      await axios.post(postUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(t("packing.packSuccess", "Box packed successfully!"));
      form.resetFields();
      setFileList([]);
      setSelectedItems({});
      onPackingComplete?.(false);
      onRefresh?.();
      fetchPackingStatus(); // Refresh status
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("packing.packFailed", "Failed to record package");
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const uploadProps = {
    onRemove: (file) => {
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false; // Prevent automatic upload
    },
    fileList,
    maxCount: 1,
    accept: "image/*",
  };

  if (loading && !packingStatus) {
    return (
      <Spin size="large" style={{ display: "block", margin: "20px auto" }} />
    );
  }

  if (!packingStatus) {
    return null;
  }


  if (
    packingStatus.blocked_reason &&
    packingStatus.blocked_reason !== "incomplete_packing"
  ) {
    const isNotAllArrived =
      packingStatus.blocked_reason === "not_all_arrived" ||
      packingStatus.message?.toLowerCase().includes("not every 1688 order");

    const displayMessage = isNotAllArrived
      ? t("packing.notEveryOrderArrived", packingStatus.message)
      : packingStatus.message;

    return (
      <Alert
        message={t("packing.blocked", "Packing Blocked")}
        description={
          <div>
            <p style={{ margin: 0 }}>{displayMessage}</p>
            {packingStatus.not_arrived_alibaba_order_ids?.length > 0 && (
              <p style={{ margin: "8px 0 0 0" }}>
                <strong>
                  {t("packing.pendingOrders", "Pending Orders:")}{" "}
                </strong>
                {packingStatus.not_arrived_alibaba_order_ids.join(", ")}
              </p>
            )}
          </div>
        }
        type="warning"
        showIcon
        style={{ marginBottom: 0 }}
      />
    );
  }

  const columns = [
    {
      title: t("packing.item", "Item / SKU"),
      key: "item",
      render: (_, record) => {
        const wegeSku = record.wege_sku;
        const order1688Id = record.order_1688_id;

        return (
          <div>
            <div>
              <Text
                strong
                style={{
                  cursor: wegeSku ? "pointer" : "default",
                  userSelect: "none",
                }}
                onDoubleClick={() =>
                  wegeSku &&
                  copyToClipboard(wegeSku, {
                    successMessage: t("copy.wegeSkuCopied", {
                      defaultValue: "Wege SKU copied",
                    }),
                  })
                }
                title={
                  wegeSku
                    ? t("copy.doubleClickToCopy", {
                        defaultValue: "Double-click to copy",
                      })
                    : ""
                }
              >
                {wegeSku || order1688Id || `Item #${record.order_item_id}`}
              </Text>
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#8c8c8c",
                marginTop: "2px",
                lineHeight: "1.4",
              }}
            >
              {order1688Id && (
                <div
                  style={{
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onDoubleClick={() =>
                    copyToClipboard(order1688Id, {
                      successMessage: t("copy.order1688IdCopied", {
                        defaultValue: "1688 ID copied",
                      }),
                    })
                  }
                  title={t("copy.doubleClickToCopy", {
                    defaultValue: "Double-click to copy",
                  })}
                >
                  <span>1688 ID: {order1688Id}</span>
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: t("packing.progress", "Packed / Total"),
      key: "progress",
      render: (_, record) => (
        <Space>
          <Text type={record.fully_packed ? "success" : "warning"}>
            {record.packed_quantity} / {record.total_quantity}
          </Text>
          {record.fully_packed && (
            <Tag color="green">{t("packing.done", "Done")}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t("packing.warnings", "Label Warnings"),
      key: "warnings",
      width: "35%",
      render: (_, record) => {
        const note = record.note_en || record.note_ar;
        if (!note) return <Text type="secondary">-</Text>;

        return (
          <Alert
            type="warning"
            message={note}
            showIcon
            style={{ padding: "4px 8px" }}
          />
        );
      },
    },
    {
      title: t("packing.packNow", "Pack in this box"),
      key: "packNow",
      width: "25%",
      render: (_, record) => {
        if (record.fully_packed) return null;

        const currentSelectedQty =
          selectedItems[record.order_item_id]?.quantity || 0;
        const note = record.note_en || record.note_ar;
        const needsConfirmation = !!note;
        const isConfirmed =
          selectedItems[record.order_item_id]?.label_changed_confirmed || false;

        return (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space>
              <Text>{t("packing.qty", "Qty:")}</Text>
              <InputNumber
                min={0}
                max={record.remaining_quantity}
                value={currentSelectedQty}
                onChange={(val) =>
                  handleItemQuantityChange(
                    record.order_item_id,
                    val || 0,
                    record.remaining_quantity,
                  )
                }
              />
            </Space>
            {needsConfirmation && currentSelectedQty > 0 && (
              <Checkbox
                checked={isConfirmed}
                onChange={(e) =>
                  handleLabelConfirmChange(
                    record.order_item_id,
                    e.target.checked,
                  )
                }
                style={{ color: "#d46b08", fontWeight: 500 }}
              >
                {t("packing.confirmLabel", "I attached the correct label")}
              </Checkbox>
            )}
          </Space>
        );
      },
    },
  ];

  if (packingStatus.ready_for_submission) {
    return (
      <div style={{ padding: "4px 0" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "14px",
            padding: "10px 14px",
            backgroundColor: "#f6ffed",
            borderRadius: "6px",
            border: "1px solid #b7eb8f",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "13px", color: "#135200", fontWeight: 600 }}>
            <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 6 }} />
            {t(
              "packing.allPackedTip",
              "All items for this order are already packed into physical box(es).",
            )}
          </span>
          {onProceedToBundle && (
            <Button
              type="primary"
              size="small"
              onClick={onProceedToBundle}
              style={{ fontWeight: 600 }}
            >
              {t("stepper.proceedToBundle", "Proceed to Bundle Shipment →")}
            </Button>
          )}
        </div>

        <Table
          dataSource={packingStatus.items}
          columns={columns}
          rowKey="order_item_id"
          pagination={false}
          size="small"
        />
      </div>
    );
  }

  return (
    <Collapse
      defaultActiveKey={["1"]}
      style={{
        marginBottom: 16,
        border: "1px solid #91caff",
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      <Collapse.Panel
        key="1"
        header={
          <>
            <InfoCircleOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            {t("packing.station", "Packing Station")}
          </>
        }
        style={{ padding: 0 }}
      >
        <Alert
          message={t("packing.incomplete", "Packing Incomplete")}
          description={t(
            "packing.instructions",
            "Please pack the remaining items into physical boxes. Enter the dimensions and weight for the current box, select which items are going inside, and confirm any label changes.",
          )}
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />

        <Table
          dataSource={packingStatus.items}
          columns={columns}
          rowKey="order_item_id"
          pagination={false}
          size="small"
          style={{ marginBottom: 24 }}
          rowClassName={(record) => (record.fully_packed ? "packed-row" : "")}
        />

        <Card
          type="inner"
          title={t("packing.boxDetails", "Current Box Details")}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Space size="large" wrap style={{ marginBottom: 16 }}>
              <Form.Item
                name="weight_kg"
                label={t("packing.weight", "Weight (kg)")}
                rules={[
                  {
                    required: true,
                    message: t("packing.reqWeight", "Required"),
                  },
                ]}
              >
                <InputNumber min={0.01} step={0.1} style={{ width: 120 }} />
              </Form.Item>

              <Form.Item
                name="length_cm"
                label={t("packing.length", "Length (cm)")}
                rules={[
                  {
                    required: true,
                    message: t("packing.reqLength", "Required"),
                  },
                ]}
              >
                <InputNumber min={1} style={{ width: 120 }} />
              </Form.Item>

              <Form.Item
                name="width_cm"
                label={t("packing.width", "Width (cm)")}
                rules={[
                  {
                    required: true,
                    message: t("packing.reqWidth", "Required"),
                  },
                ]}
              >
                <InputNumber min={1} style={{ width: 120 }} />
              </Form.Item>

              <Form.Item
                name="height_cm"
                label={t("packing.height", "Height (cm)")}
                rules={[
                  {
                    required: true,
                    message: t("packing.reqHeight", "Required"),
                  },
                ]}
              >
                <InputNumber min={1} style={{ width: 120 }} />
              </Form.Item>
            </Space>

            <Form.Item label={t("packing.photo", "Box Photo")} required>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>
                  {t("packing.selectPhoto", "Select Photo")}
                </Button>
              </Upload>
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              loading={submitLoading}
              size="large"
            >
              {t("packing.packBoxBtn", "Record Packed Box")}
            </Button>
          </Form>
        </Card>

        <style>{`
        .packed-row {
          background-color: #f6ffed;
          opacity: 0.7;
        }
      `}</style>
      </Collapse.Panel>
    </Collapse>
  );
};

export default PackingStation;
